import { memo, useEffect, useRef, useState } from "react";
import { useDragLayer, XYCoord } from "react-dnd";

export const FormElementDragLayer = memo(function FormElementDragLayer() {
  // Track snap-back animation state
  const [animationPhase, setAnimationPhase] = useState<
    "idle" | "dragging" | "snap-back"
  >("idle");
  const [snapBackPosition, setSnapBackPosition] = useState<XYCoord | null>(
    null,
  );
  const lastOffsetRef = useRef<XYCoord | null>(null);
  const initialOffsetRef = useRef<XYCoord | null>(null);
  const lastDragEventRef = useRef<number>(0);
  const staleCheckIntervalRef = useRef<number | null>(null);
  const itemRef = useRef<{ element: React.ReactNode } | null>(null);
  const wasDroppedRef = useRef<boolean>(false);

  const { isDragging, item, currentOffset, initialSourceClientOffset } =
    useDragLayer((monitor) => ({
      item: monitor.getItem(),
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging(),
      initialSourceClientOffset: monitor.getInitialSourceClientOffset(),
    }));

  // Track the initial offset when drag starts (for snap-back target)
  useEffect(() => {
    if (isDragging && initialSourceClientOffset && !initialOffsetRef.current) {
      initialOffsetRef.current = initialSourceClientOffset;
    }
    if (!isDragging && animationPhase === "idle") {
      initialOffsetRef.current = null;
    }
  }, [isDragging, initialSourceClientOffset, animationPhase]);

  // Track the last known offset and item
  useEffect(() => {
    if (currentOffset) {
      lastOffsetRef.current = currentOffset;
    }
    if (item) {
      itemRef.current = item;
    }
  }, [currentOffset, item]);

  // Handle drag state changes
  useEffect(() => {
    if (
      isDragging &&
      (animationPhase === "idle" || animationPhase === "snap-back")
    ) {
      setAnimationPhase("dragging");
      lastDragEventRef.current = Date.now();
      // Reset snap-back state if interrupting an animation
      setSnapBackPosition(null);
    }
  }, [isDragging, animationPhase]);

  // Listen for native drag events which fire continuously while dragging
  useEffect(() => {
    if (animationPhase !== "dragging") {
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
        staleCheckIntervalRef.current = null;
      }
      return;
    }

    // Reset drop tracking for new drag
    wasDroppedRef.current = false;

    const handleDragOver = () => {
      lastDragEventRef.current = Date.now();
    };

    const handleDrop = () => {
      // Mark that a successful drop occurred - skip snap-back animation
      wasDroppedRef.current = true;
      setAnimationPhase("idle");
      setSnapBackPosition(null);
      initialOffsetRef.current = null;
      lastOffsetRef.current = null;
      itemRef.current = null;
    };

    // dragover fires continuously (~60fps) while dragging, even when mouse is stationary
    document.addEventListener("dragover", handleDragOver);
    // drop fires when element is dropped on a valid drop target
    document.addEventListener("drop", handleDrop);

    // Start checking for stale drag events
    staleCheckIntervalRef.current = window.setInterval(() => {
      const timeSinceLastEvent = Date.now() - lastDragEventRef.current;
      // If no dragover event in 150ms while isDragging is true, mouse was released
      // Only trigger snap-back if it wasn't a successful drop
      if (timeSinceLastEvent > 150 && !wasDroppedRef.current) {
        // Trigger snap-back animation
        // First, ensure we're at the last known position, then animate to initial
        setSnapBackPosition(lastOffsetRef.current);
        setAnimationPhase("snap-back");
      }
    }, 50);

    return () => {
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("drop", handleDrop);
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
        staleCheckIntervalRef.current = null;
      }
    };
  }, [animationPhase]);

  // Handle snap-back animation
  useEffect(() => {
    if (animationPhase !== "snap-back") {
      return;
    }

    // Use requestAnimationFrame to ensure the element is rendered at current position
    // before we start the transition to the initial position
    const frameId = requestAnimationFrame(() => {
      // Now animate to initial position
      setSnapBackPosition(initialOffsetRef.current);
    });

    // Clean up after animation completes
    const timeoutId = setTimeout(() => {
      setAnimationPhase("idle");
      setSnapBackPosition(null);
      initialOffsetRef.current = null;
      lastOffsetRef.current = null;
      itemRef.current = null;
    }, 450); // 400ms animation + 50ms buffer

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(timeoutId);
    };
  }, [animationPhase]);

  // Determine what to render
  const shouldRender =
    animationPhase === "dragging" || animationPhase === "snap-back";

  if (!shouldRender) {
    return null;
  }

  const displayItem = animationPhase === "snap-back" ? itemRef.current : item;
  if (!displayItem) {
    return null;
  }

  const displayOffset =
    animationPhase === "snap-back" ? snapBackPosition : currentOffset;

  const isAnimating =
    animationPhase === "snap-back" &&
    snapBackPosition !== lastOffsetRef.current;

  const transform = displayOffset
    ? `translate(${displayOffset.x}px, ${displayOffset.y}px)`
    : undefined;

  return (
    <div
      style={{
        position: "fixed",
        pointerEvents: "none",
        zIndex: 9999,
        left: 0,
        top: 0,
      }}
    >
      <div
        style={{
          transform,
          WebkitTransform: transform,
          visibility: displayOffset ? "visible" : "hidden",
          transition: isAnimating
            ? "transform 0.4s ease-out, opacity 0.4s ease-out"
            : undefined,
          opacity: isAnimating ? 0 : 1,
          display: "flex",
          alignItems: "center",
          gap: "var(--redo-spacing-md)",
          borderRadius: "var(--border-radius-full)",
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          backdropFilter: "blur(8px)",
          border: "1px solid var(--redo-colors-border-border-primary)",
          padding: "var(--redo-spacing-xs) var(--redo-spacing-lg)",
          width: "max-content",
        }}
      >
        {displayItem.element}
      </div>
    </div>
  );
});
